import React, { useEffect, useRef, useState } from 'react';
import Chart from 'react-apexcharts';
import * as MinistryReportApi from '../../api/MinistryReportApi';
import { toast } from 'react-toastify';
import IncidentListingModal from './IncidentListingModal';

const colorMapping = [
  { keyword: 'server', color: 'rgb(26,26,26)' },
  { keyword: 'administrative', color: 'rgb(33,90,108)' },
  { keyword: 'aadhar', color: 'rgb(117,57,0)' },
  { keyword: 'node', color: 'rgb(71,55,33)' },
  { keyword: 'syncerror', color: 'rgb(36,112,226)' },
  { keyword: 'power', color: 'rgb(18,115,75)' },
  { keyword: 'wlan', color: 'rgb(177,23,2)' },
  { keyword: 'internetissue', color: 'rgb(228,139,243)' },
  { keyword: 'law', color: 'rgb(9,83,168)' },
  { keyword: 'cybersecurity', color: 'rgb(90,50,133)' },
  { keyword: 'training', color: 'rgb(32,99,199)' },
];

const getColorForReason = (reason) => {
  const normalizedCategory = reason.toLowerCase().replace(/[^a-z0-9]/g, '');
  const match = colorMapping.find(({ keyword }) =>
    normalizedCategory.includes(keyword)
  );
  return match ? match.color : 'rgb(100,100,100)'; // Default gray color
};

const BarChart = ({
  data = [],
  height = 230,
  width = 450,
  selectedDatee,
  selectedShift,
}) => {
  const uniqueLegendItems = [
    ...new Set(
      data.map((item) => {
        const color = getColorForReason(item.reason);
        return { reason: item.reason, color };
      })
    ),
  ];

  const DEFAULT_INCIDENT_DETAILS_MODAL = {
    center_id: null,
    show: false,
    title: 'Incident Details',
  };
  const [showIncidentDetailsModal, setShowIncidentDetailsModal] = useState(
    DEFAULT_INCIDENT_DETAILS_MODAL
  );

  const [selectedReason, setSelectedReason] = useState(null);

  const dataRef = useRef(data);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  const [modalData, setModalData] = useState([]);

  const [filter, setFilter] = useState({
    reason: '',
    date: '',
    shift: '',
  });

  const fetchModalData = async () => {
    const response = await MinistryReportApi.getReasonCategoryIncidents(filter);

    if (response?.success) {
      setModalData(response?.data);
    } else {
      toast.error(`Error fetching stage incidents:  ${response?.message}`);
    }
  };

  useEffect(() => {
    if (filter.reason) {
      fetchModalData();
    }
  }, [filter.reason]);

  useEffect(() => {
    setFilter((prev) => ({
      stage: '',
      date: selectedDatee,
      shift: selectedShift,
    }));
  }, [selectedDatee, selectedShift]);

  const options = {
    chart: {
      type: 'bar',
      height: 350,
      toolbar: { show: false },
      zoom: { enabled: false },
      pan: { enabled: false },
      events: {
        dataPointSelection: (event, chartContext, config) => {
          const clickedStage = dataRef.current[config?.dataPointIndex].reason;
          setSelectedReason(clickedStage);

          setFilter((prev) => ({
            ...prev,
            reason: clickedStage,
          }));
          setShowIncidentDetailsModal((prev) => ({ ...prev, show: true }));
        },
      },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: '50%',
        distributed: true, // Ensures different colors for each bar
      },
    },
    colors: data.map((item) => getColorForReason(item.reason)), // Assign colors dynamically
    dataLabels: { enabled: false },
    xaxis: {
      categories: data.map((item) => item.reason),
      labels: { show: false }, // Hide left labels
    },
    yaxis: {
      labels: { show: true },
    },
    tooltip: {
      enabled: true,
      y: {
        formatter: (value, { dataPointIndex }) => {
          const item = data[dataPointIndex];
          return item
            ? `Incidents: ${value} - ${item.reason}`
            : `Incidents: ${value}`;
        },
      },
    },
    legend: { show: false }, // Hide default legend
  };

  const series = [{ name: 'Count', data: data.map((item) => item.count) }];
  if (!data || data.length === 0) {
    return (
      <div
        style={{ height: height + 50, width: '100%' }}
        className='d-flex justify-content-center align-items-center text-center'
      >
        No data available, Please select exam day/shift filters to see past exam
        days data.
      </div>
    );
  }

  return (
    <div style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
      <Chart
        options={options}
        series={series}
        type='bar'
        height={height}
        width={width}
      />
      {/* Custom Legend */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
        }}
      >
        {uniqueLegendItems.map(({ reason, color }, index) => (
          <div
            key={index}
            style={{
              display: 'flex',
              alignItems: 'center',
              marginRight: '15px',
            }}
          >
            <span
              style={{
                width: '12px',
                height: '12px',
                backgroundColor: color,
                display: 'inline-block',
                marginRight: '5px',
              }}
            ></span>
            <span style={{ fontSize: '14px' }}>{reason}</span>
          </div>
        ))}
      </div>
      {showIncidentDetailsModal.show && (
        <IncidentListingModal
          showIncidentDetailsModal={showIncidentDetailsModal}
          setShowIncidentDetailsModal={setShowIncidentDetailsModal}
          incidents={modalData}
          selectedItemName={selectedReason}
        />
      )}
    </div>
  );
};

export default BarChart;
